import { Store } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { QueryClient } from "react-query";
import { Persistor } from "redux-persist";

import {
  SESSION_INVALIDATED_CODE,
  SESSION_INVALIDATED_BY_LOGOUT_CODE,
  SESSION_INVALIDATED_MESSAGE,
  isSessionInvalidatedError as isSessionInvalidatedMessage,
} from "../constant/sessionConstants";
import { performStateCleanup } from "../services/standardizedLogout";
import { showSessionKickedOverlay } from "./sessionKickedOverlay";
import {
  claimSessionInvalidation,
  resetSessionInvalidation,
  isLoggingOut,
  setLoggingOut,
} from "./sessionState";

/**
 * Custom error thrown when a session is invalidated by a new login.
 * React Query global error handlers should filter this out to avoid
 * spurious error toasts during session cleanup.
 */
export class SessionInvalidatedError extends Error {
  constructor() {
    super("Session invalidated");
    this.name = "SessionInvalidatedError";
  }
}

interface SetupInterceptorParams {
  store: Store;
  resetActions?: Array<() => { type: string }>;
  persistor?: Persistor;
  queryClient?: QueryClient;
}

let isInterceptorSetup = false;
let interceptorId: number | null = null;

// Re-export setLoggingOut so existing callers (logout services) still work
export { setLoggingOut };

/**
 * Sets up an Axios response interceptor to handle session invalidation.
 * This should be called once when the app initializes.
 */
export const setupSessionInterceptor = ({
  store,
  resetActions,
  persistor,
  queryClient,
}: SetupInterceptorParams): void => {
  // Prevent duplicate interceptors
  if (isInterceptorSetup && interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        const data = error.response.data as { code?: string; message?: string };

        // Check for session invalidation by code OR by message content
        const isSessionInvalidated =
          data?.code === SESSION_INVALIDATED_CODE ||
          data?.code === SESSION_INVALIDATED_BY_LOGOUT_CODE ||
          (data?.message && isSessionInvalidatedMessage(data.message));

        if (isSessionInvalidated) {
          // If user is intentionally logging out, suppress the notification and let logout handle cleanup
          if (isLoggingOut()) {
            setLoggingOut(false);
            return Promise.reject(error);
          }

          // Try to claim the invalidation lock — if someone else (WebSocket) already claimed it, just reject quietly
          if (!claimSessionInvalidation()) {
            return Promise.reject(new SessionInvalidatedError());
          }

          // Wipe local state clean immediately (so refresh won't restore the session)
          await performStateCleanup({ store, resetActions, persistor, queryClient });

          // For logout events, silently redirect — no overlay needed
          if (data?.code === SESSION_INVALIDATED_BY_LOGOUT_CODE) {
            resetSessionInvalidation();
            window.location.href = "/login";
            return Promise.reject(new SessionInvalidatedError());
          }

          // For new login from another device, show overlay
          showSessionKickedOverlay(SESSION_INVALIDATED_MESSAGE, () => {
            resetSessionInvalidation();
            window.location.href = "/login";
          });

          return Promise.reject(new SessionInvalidatedError());
        }
      }
      return Promise.reject(error);
    },
  );

  isInterceptorSetup = true;
};

/**
 * Removes the session interceptor. Call this on cleanup if needed.
 */
export const removeSessionInterceptor = (): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
    isInterceptorSetup = false;
    resetSessionInvalidation();
  }
};

export default setupSessionInterceptor;
